import clsx from "clsx";
import { useEffect, useRef, useState } from "react";

import type { ChildDisplayStatus } from "../types";

import {
  BaseArchiveIcon,
  CopyCheckIcon,
  DotsVerticalIcon,
  EyeIcon,
  LinkIcon,
  PlusIcon,
} from "../../Icons";
import { PopoverMenu } from "../../PopoverMenu";
import { Typo } from "../../Typo";

export type KebabAction =
  | "view"
  | "view-program"
  | "view-analytics"
  | "copy-link"
  | "create-program"
  | "archive-program";

type MenuItem = {
  id: KebabAction;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  danger?: boolean;
  /** Confirms in place instead of closing the menu — see `copied` below. */
  copyAction?: boolean;
};

/** How long "Copied" shows before reverting, per the canvas's KebabMenu. */
const COPIED_RESET_MS = 3000;

type KebabMenuProps = {
  kind: "parent" | "child";
  status: ChildDisplayStatus;
  hasActiveProgram?: boolean;
  isMultiHoid?: boolean;
  onAction: (id: KebabAction) => void;
};

const iconClass = "h-4 w-4";

export const KebabMenu = ({
  kind,
  status,
  hasActiveProgram = true,
  isMultiHoid = false,
  onAction,
}: KebabMenuProps) => {
  // Copy confirms in place: the item swaps to a green check + "Copied" for 3s
  // rather than closing the menu (canvas `KebabMenu` → `handleItem`).
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (copyTimer.current) clearTimeout(copyTimer.current);
    },
    [],
  );

  const confirmCopy = () => {
    setCopied(true);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(false), COPIED_RESET_MS);
  };

  const copyDisabled = status === "pendingKyc";
  const copyItem = {
    id: "copy-link" as const,
    label: copied ? "Copied" : "Copy referral link",
    icon: copied ? (
      <CopyCheckIcon className={iconClass} />
    ) : (
      <LinkIcon className={iconClass} />
    ),
    disabled: copyDisabled,
    copyAction: true,
  };

  const items: MenuItem[] = [];
  if (kind === "parent") {
    items.push({
      id: "view",
      label: "View Sub IB detail",
      icon: <EyeIcon className={iconClass} />,
    });
    // Hidden for multi-HOID Sub IBs — there's no single canonical link to
    // copy; the user expands the row and copies from a child row instead.
    if (!isMultiHoid) {
      items.push(copyItem);
    }
    items.push({
      id: "create-program",
      label: "Create new rebate program",
      icon: <PlusIcon className={iconClass} />,
    });
    if (hasActiveProgram) {
      items.push({
        id: "archive-program",
        label: "Archive rebate program",
        // Canvas `.menu__item--danger .menu__icon { background: --icon-error }`.
        icon: <BaseArchiveIcon className={`${iconClass} text-icon-error`} />,
        danger: true,
      });
    }
  } else {
    items.push({
      id: "view-program",
      label: "View program detail",
      icon: <EyeIcon className={iconClass} />,
    });
    items.push(copyItem);
    if (status !== "archived") {
      items.push({
        id: "archive-program",
        label: "Archive rebate program",
        // Canvas `.menu__item--danger .menu__icon { background: --icon-error }`.
        icon: <BaseArchiveIcon className={`${iconClass} text-icon-error`} />,
        danger: true,
      });
    }
  }

  return (
    <PopoverMenu
      place="bottom"
      // See the programs-tab kebab — DS opens the panel rightward.
      align="start"
      asChild
      content={
        <div className="flex w-56 flex-col gap-0.5 p-2">
          {items.map((item, i) => (
            <div key={item.id}>
              {item.danger && items[i - 1] && !items[i - 1].danger && (
                <div className="my-1 border-t border-stroke-primary" />
              )}
              <button
                type="button"
                disabled={item.disabled}
                onClick={() => {
                  if (item.disabled) return;
                  onAction(item.id);
                  if (item.copyAction) confirmCopy();
                }}
                className={clsx(
                  "flex w-full items-center gap-2 rounded-md px-3 py-2 text-left hover:bg-surface-tonal-default",
                  item.disabled && "cursor-not-allowed opacity-50",
                )}
              >
                {item.icon}
                <Typo
                  element="span"
                  type="bodySM"
                  textColor={
                    item.danger
                      ? "text-text-negative-default"
                      : "text-text-primary"
                  }
                >
                  {item.label}
                </Typo>
              </button>
            </div>
          ))}
        </div>
      }
    >
      <button
        type="button"
        aria-label="More actions"
        className="flex h-8 w-8 items-center justify-center rounded-full text-icon-brand-default hover:bg-overlay-opacity-state-selected"
      >
        <DotsVerticalIcon className={iconClass} />
      </button>
    </PopoverMenu>
  );
};
